import { Building2, CalendarClock, Route, ShieldAlert, TrendingDown } from "lucide-react";

import { ColocaKpiCard } from "@/shared/components/data-display/ColocaKpiCard";
import useTranslations from "@/shared/lang/useTranslations";

import type { CorridorKpis } from "../../hooks/usePaymentFees";
import type { CorridorFilter } from "../../types/paymentFees.types";

export interface FeeKpiStripProps {
  kpis: CorridorKpis;
  filter: CorridorFilter;
  onFilterChange: (filter: CorridorFilter) => void;
  scoped: boolean;
  /** The corridor catalog failed or was denied — no count can be trusted. */
  unavailable: boolean;
}

/** The strip IS the corridors filter: each tile narrows the table to what it counts. */
export function FeeKpiStrip({ kpis, filter, onFilterChange, scoped, unavailable }: FeeKpiStripProps) {
  const { t } = useTranslations("paymentFees");
  const value = (count: number) => (unavailable ? "—" : String(count));

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
      <ColocaKpiCard
        label={t("kpi_all_label")}
        value={value(kpis.total)}
        icon={Route}
        accent="primary"
        meta={unavailable ? t("kpi_unavailable") : t("kpi_all_meta", { priced: kpis.priced })}
        selected={filter === "all"}
        onSelect={() => onFilterChange("all")}
      />
      <ColocaKpiCard
        label={t("kpi_unpriced_label")}
        value={value(kpis.unpriced)}
        icon={ShieldAlert}
        accent={kpis.unpriced > 0 ? "danger" : "success"}
        meta={kpis.unpriced > 0 ? t("kpi_unpriced_meta") : t("kpi_unpriced_ok")}
        selected={filter === "unpriced"}
        onSelect={() => onFilterChange("unpriced")}
      />
      <ColocaKpiCard
        label={t(scoped ? "kpi_special_label_org" : "kpi_special_label_general")}
        value={value(kpis.special)}
        icon={Building2}
        accent={kpis.special > 0 ? "info" : "neutral"}
        meta={t(scoped ? "kpi_special_meta_org" : "kpi_special_meta_general")}
        selected={filter === "special"}
        onSelect={() => onFilterChange("special")}
      />
      <ColocaKpiCard
        label={t("kpi_negative_label")}
        value={value(kpis.negative)}
        icon={TrendingDown}
        accent={kpis.negative > 0 ? "danger" : "neutral"}
        meta={t("kpi_negative_meta")}
        selected={filter === "negative"}
        onSelect={() => onFilterChange("negative")}
      />
      <ColocaKpiCard
        label={t("kpi_scheduled_label")}
        value={value(kpis.scheduled)}
        icon={CalendarClock}
        accent={kpis.scheduled > 0 ? "info" : "neutral"}
        meta={t("kpi_scheduled_meta")}
        selected={filter === "scheduled"}
        onSelect={() => onFilterChange("scheduled")}
      />
    </div>
  );
}
